/**
 * Admin → Re-interview campaign panel.
 *
 * Shown as a top-level admin tab. Lists trial users who never converted,
 * lets the founder hand-pick recipients, optionally include a "30 free days"
 * reward (via /api/auth/trial/extend30/<token>), and bulk-send the
 * re-interview email.
 *
 * Backend: /api/admin/reinterview/{candidates,send}
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, Gift, Loader2, RefreshCw, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

const STATUS_TONES = {
  trial:           'bg-blue-100 text-blue-800',
  trial_registered:'bg-blue-100 text-blue-800',
  trial_expired:   'bg-amber-100 text-amber-800',
  expired:         'bg-amber-100 text-amber-800',
  cancelled:       'bg-rose-100 text-rose-800',
  inactive:        'bg-slate-100 text-slate-700',
  unknown:         'bg-slate-100 text-slate-700',
};

export default function ReinterviewPanel({ adminToken }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState({});  // practiceId -> bool
  const [includeFreeMonth, setIncludeFreeMonth] = useState(true);
  const [customNote, setCustomNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('');
  const [hideAlreadySent, setHideAlreadySent] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/reinterview/candidates`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        cache: 'no-store',
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      const data = await res.json();
      setCandidates(data.candidates || []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return candidates.filter((c) => {
      if (hideAlreadySent && c.reinterviewEmailSentAt) return false;
      if (!q) return true;
      return (
        (c.email || '').toLowerCase().includes(q) ||
        (c.practiceName || '').toLowerCase().includes(q) ||
        (c.firstName || '').toLowerCase().includes(q) ||
        (c.lastName || '').toLowerCase().includes(q)
      );
    });
  }, [candidates, filter, hideAlreadySent]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected]);
  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected[c.practiceId]);

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const next = { ...selected };
      visible.forEach((c) => { delete next[c.practiceId]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      visible.forEach((c) => { next[c.practiceId] = true; });
      setSelected(next);
    }
  };

  const sendNow = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Send the re-interview email to ${selectedIds.length} ${selectedIds.length === 1 ? 'practice' : 'practices'}${includeFreeMonth ? ' (with 30 free days)' : ''}?`)) {
      return;
    }
    setSending(true); setResult(null); setErr('');
    try {
      const res = await fetch(`${API}/api/admin/reinterview/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practiceIds: selectedIds,
          includeFreeMonth,
          customNote: customNote.trim() || null,
        }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      const data = await res.json();
      setResult(data);
      setSelected({});
      // Refresh candidate list so newly-sent rows show their stamp
      load();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-reinterview-panel" className="space-y-4">
      {/* Header & controls */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-indigo-600 text-white rounded-lg p-3"><Mail className="w-6 h-6" /></div>
            <div>
              <h2 className="text-xl font-extrabold text-indigo-900">Re-interview Campaign</h2>
              <p className="text-sm text-indigo-800 mt-1 max-w-2xl">
                Hand-pick trial users who never converted and send a personal
                founder note asking what was missing. Optionally offer
                <strong> 30 free days</strong> as a thank-you (extends their trial via
                <code className="bg-white px-1.5 py-0.5 rounded text-[11px] mx-1 border border-indigo-200">/trial/extend30</code>).
              </p>
            </div>
          </div>
          <button
            data-testid="reinterview-refresh"
            onClick={load}
            className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-bold"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Campaign options */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="reinterview-include-free-month"
            checked={includeFreeMonth}
            onChange={(e) => setIncludeFreeMonth(e.target.checked)}
            className="w-5 h-5 accent-emerald-600"
          />
          <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Gift className="w-4 h-4 text-emerald-600" />
            Include "30 free days" reward in the email
          </span>
        </label>
        <div>
          <label htmlFor="reinterview-custom-note" className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
            Custom intro note (optional — appears as italic blue quote at top of the email)
          </label>
          <textarea
            id="reinterview-custom-note"
            data-testid="reinterview-custom-note"
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            rows={2}
            placeholder='e.g., "I noticed you signed up two weeks ago — wanted to check in personally."'
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
        <input
          data-testid="reinterview-search"
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, email, or practice…"
          className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <input
            type="checkbox"
            checked={hideAlreadySent}
            onChange={(e) => setHideAlreadySent(e.target.checked)}
            className="w-4 h-4 accent-indigo-600"
          />
          Hide already-sent
        </label>
        <div className="text-sm text-slate-600">
          <strong className="text-slate-900">{visible.length}</strong> shown ·{' '}
          <strong className="text-indigo-700">{selectedIds.length}</strong> selected
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div
          data-testid="reinterview-result"
          className={`rounded-xl p-4 border-2 flex items-start gap-3 ${
            result.failed > 0 ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'
          }`}
        >
          {result.failed > 0
            ? <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            : <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />}
          <div className="text-sm">
            <p className="font-bold text-slate-900">
              Sent {result.sent} email{result.sent === 1 ? '' : 's'}
              {result.failed > 0 ? `, ${result.failed} failed` : ''}.
            </p>
            <p className="text-slate-700 mt-0.5">
              Each recipient who clicks "Claim My 30 Free Days" will have their trial silently extended by 30 days.
            </p>
          </div>
        </div>
      )}

      {/* Error banner */}
      {err && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Candidates table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading candidates…
          </div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No candidates match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      data-testid="reinterview-select-all"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      className="w-4 h-4 accent-indigo-600 cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Practice / Owner</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Email</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Status</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Logins</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Signed Up</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Re-interview</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const isSel = !!selected[c.practiceId];
                  const tone = STATUS_TONES[c.subscriptionStatus] || STATUS_TONES.unknown;
                  return (
                    <tr
                      key={c.practiceId}
                      className={`border-b border-slate-100 hover:bg-indigo-50/40 ${isSel ? 'bg-indigo-50/60' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`reinterview-row-${c.practiceId}`}
                          checked={isSel}
                          onChange={() => setSelected({ ...selected, [c.practiceId]: !isSel })}
                          className="w-4 h-4 accent-indigo-600 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-bold text-slate-900">{c.practiceName}</div>
                        <div className="text-xs text-slate-500">
                          {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{c.email}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${tone}`}>
                          {c.subscriptionStatus}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{c.loginCount}</td>
                      <td className="px-3 py-2 text-slate-700">{fmtDate(c.createdAt)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {c.reinterviewEmailSentAt ? (
                          <span className="text-emerald-700 font-medium" title={c.reinterviewEmailSentAt}>
                            Sent {fmtDate(c.reinterviewEmailSentAt)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        {c.trialExtended30 && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                            +30d claimed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sticky send bar */}
      <div className="sticky bottom-0 bg-white border-2 border-indigo-300 rounded-xl p-4 shadow-lg flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-slate-700">
          {selectedIds.length === 0 ? (
            <span>Select one or more practices to send.</span>
          ) : (
            <span>
              Ready to send to <strong className="text-indigo-700">{selectedIds.length}</strong>{' '}
              {selectedIds.length === 1 ? 'practice' : 'practices'}
              {includeFreeMonth ? ' with the 30-free-days reward' : ''}.
            </span>
          )}
        </div>
        <button
          data-testid="reinterview-send-button"
          onClick={sendNow}
          disabled={!selectedIds.length || sending}
          className={`flex items-center gap-2 px-5 py-3 rounded-lg font-bold text-white transition-colors ${
            !selectedIds.length || sending
              ? 'bg-slate-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? 'Sending…' : `Send Re-interview Email${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
        </button>
      </div>
    </div>
  );
}
